<script setup lang="ts">
import { Heading } from "~/components/clarity-ui/Heading";
import { Tag } from "~/components/clarity-ui/Tag";
import { TagGroup } from "~/components/clarity-ui/Tag/TagGroup";
import { ParallaxContainer } from "~/components/ui/ParallaxContainer";
import usePromoTracking from "~/composables/tracking/usePromoTracking";
import LinkComponent from "~/templates/components/cms/LinkComponent.vue";
import PictureElement from "~/templates/elements/PictureElement.vue";
import type { mgnlHeadline } from "~/types/magnolia";
import { DIMENSIONS } from "~/utils/helper/imaging";

export interface CategoryTileComponentProps {
  headline?: mgnlHeadline;
  image?: object;
  link?: object;
  metadata?: object;
  tags?: [];
}

const props = defineProps<CategoryTileComponentProps>();

const categoryTile = ref(null);
const { trackPromoView, trackPromoClick } = usePromoTracking({
  metadata: props?.metadata,
  creativeName: props?.headline?.text,
});

const imageBreakpoints = {
  start: DIMENSIONS["start"].width["10/12"].ratio["3/4"].scale["1.5"],
  sm: DIMENSIONS["sm"].width["10/12"].ratio["3/4"].scale["1.5"],
  md: DIMENSIONS["md"].width["1/2"].ratio["3/4"].scale["1.5"],
  xl: DIMENSIONS["xl"].width["1/4"].ratio["3/4"].scale["1.5"],
  container: DIMENSIONS["container"].width["1/4"].ratio["3/4"].scale["1.5"],
};

onMounted(() => {
  trackPromoView(categoryTile);
});
</script>

<template>
  <div ref="categoryTile" class="group relative w-full overflow-hidden rounded-2xl">
    <ParallaxContainer class="relative inset-0" debug>
      <PictureElement
        :breakpoints="imageBreakpoints"
        :img="image"
        img-class="aspect-3/4 object-cover motion-safe:transition-transform motion-safe:group-hover:scale-110 motion-safe:duration-300 w-full h-full"
      />
    </ParallaxContainer>
    <div
      class="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-black to-transparent opacity-60"
    />
    <div
      class="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black to-transparent opacity-60"
    />
    <div
      class="absolute inset-0 flex h-full flex-col items-start justify-between overflow-hidden p-6 text-white"
    >
      <LinkComponent :link="link" class="stretched-link" @click="trackPromoClick">
        <Heading v-if="headline.text" color="light" size="medium" :tag="headline?.tag ?? 'h3'">{{
          headline.text
        }}</Heading>
      </LinkComponent>
      <TagGroup color="light" size="small" outlined>
        <Tag v-for="tag in tags" :key="tag['@id']">{{ tag?.value }}</Tag>
      </TagGroup>
    </div>
  </div>
</template>
